// Firebase Service - Sikker database-integrasjon
import offlineQueue from './offlineQueue';
import { compressImage } from './imageService';
import weatherService from './weatherService';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  onAuthStateChanged, 
  signInAnonymously, 
  signInWithCustomToken,
  signInWithPopup,
  GoogleAuthProvider,
  signOut 
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  addDoc, 
  onSnapshot, 
  deleteDoc, 
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  writeBatch,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';

class FirebaseService {
  constructor() {
    // Firebase config fra environment variabler
    const firebaseConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
    };

    this.app = initializeApp(firebaseConfig);
    this.auth = getAuth(this.app);
    // Firestore med lokal offline-cache (IndexedDB). Da beholdes fangst-dataen
    // på enheten og vises også uten nett, etter at appen lukkes og åpnes igjen.
    // Faller tilbake til vanlig getFirestore hvis nettleseren ikke støtter det
    // (f.eks. eldre nettlesere eller privat modus som blokkerer IndexedDB).
    try {
      this.db = initializeFirestore(this.app, {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
        }),
      });
    } catch (err) {
      console.warn('Firestore offline-cache utilgjengelig, bruker standard:', err);
      this.db = getFirestore(this.app);
    }
    this.storage = getStorage(this.app);
    this.appId = process.env.REACT_APP_APP_ID || 'fiskelykke-pro-v2';
    this.analytics = null; // settes først når bruker har samtykket
  }

  // Sikker autentisering
  async initializeAuth() {
    try {
      // Sjekk om vi har en initial token
      const initialToken = process.env.REACT_APP_INITIAL_AUTH_TOKEN;
      
      if (initialToken) {
        await signInWithCustomToken(this.auth, initialToken);
      } else {
        await signInAnonymously(this.auth);
      }
    } catch (error) {
      console.error('Autentisering feilet:', error);
      throw new Error('Kunne ikke logge inn');
    }
  }

  // Google Sign In
  async signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    const popupPromise = signInWithPopup(this.auth, provider);

    // Sikkerhetsnett: Firebase sin signInWithPopup kan i enkelte tilfeller
    // (nettleserutvidelser, adblockere, eller når popupen avbrytes på uvanlig
    // vis) aldri løse eller avvise løftet. Uten denne timeouten blir
    // "signingIn"-tilstanden hengende for alltid, og brukeren må restarte
    // siden for å komme videre. Vi tvinger derfor en avvisning etter 45s.
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('TIMEOUT'));
      }, 45000);
    });

    try {
      const result = await Promise.race([popupPromise, timeoutPromise]);
      return result.user;
    } catch (error) {
      console.error('Google innlogging feilet:', error);

      // Hindre "unhandled promise rejection" hvis popup-løftet feiler etter
      // at timeouten allerede har vunnet kappløpet.
      popupPromise.catch(() => {});

      if (error.message === 'TIMEOUT') {
        throw new Error('Innloggingen tok for lang tid. Prøv igjen.');
      }
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        throw new Error('Innlogging avbrutt');
      }
      throw new Error('Kunne ikke logge inn med Google');
    }
  }

  // Lytt til auth-endringer
  onAuthStateChange(callback) {
    return onAuthStateChanged(this.auth, callback);
  }

  // Logg ut
  async signOut() {
    try {
      // Tøm lokal bildecache slik at private fangstbilder ikke blir liggende
      // igjen på enheten etter utlogging (viktig på delte enheter).
      this.clearImageCache();
      await signOut(this.auth);
    } catch (error) {
      console.error('Utlogging feilet:', error);
    }
  }

  // Be service workeren tømme bildecachen. No-op hvis SW ikke er aktiv
  // (f.eks. i utvikling eller hvis nettleseren ikke støtter service workers).
  clearImageCache() {
    try {
      navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_IMAGE_CACHE' });
    } catch (err) {
      console.warn('Kunne ikke tømme bildecache:', err);
    }
  }

  // Valider og rens fangstdata før lagring
  validateCatchData(catchData) {
    const required = ['species', 'image', 'caughtAt'];
    const missing = required.filter(field => !catchData[field]);
    
    if (missing.length > 0) {
      throw new Error(`Manglende påkrevde felt: ${missing.join(', ')}`);
    }

    // Rens og valider data
    const cleaned = {
      species: this.sanitizeString(catchData.species, 50),
      latin: this.sanitizeString(catchData.latin || '', 100),
      nickname: this.sanitizeString(catchData.nickname || '', 60),
      weight: this.sanitizeNumber(catchData.weight),
      waterType: ['salt', 'fresh'].includes(catchData.waterType) ? catchData.waterType : 'salt',
      image: this.validateImageData(catchData.image),
      caughtAt: this.validateTimestamp(catchData.caughtAt),
      time: this.sanitizeString(catchData.time || '', 10),
      privacy: ['Nøyaktig', '100m', '1km', 'Ingen'].includes(catchData.privacy) ? catchData.privacy : 'Ingen',
      weather: this.sanitizeWeatherData(catchData.weather || {}),
      advanced: this.sanitizeAdvancedData(catchData.advanced || {}),
      createdAt: Date.now(),
      userId: this.auth.currentUser?.uid
    };

    // Legg til location hvis den finnes
    if (catchData.location && catchData.location.lat && catchData.location.lng) {
      cleaned.location = {
        lat: parseFloat(catchData.location.lat),
        lng: parseFloat(catchData.location.lng)
      };
    }

    return cleaned;
  }

  // Lagre fangst
  async saveCatch(catchData) {
    if (!this.auth.currentUser) {
      throw new Error('Ikke innlogget');
    }

    try {
      const validatedData = this.validateCatchData(catchData);

      // Prøv å lagre direkte til Firebase
      if (navigator.onLine) {
        const catchesRef = collection(
          this.db, 
          'artifacts', 
          this.appId, 
          'users', 
          this.auth.currentUser.uid, 
          'catches'
        );

        const base64Image = validatedData.image;

        // Generer dokument-ID på forhånd slik at vi kan laste opp bildet til
        // Storage FØR dokumentet opprettes. Da skriver vi aldri et tomt
        // image-felt, som ville brutt Firestore-regelen (image.size() > 0).
        const docRef = doc(catchesRef);
        validatedData.createdAt = serverTimestamp();

        try {
          validatedData.image = await this.uploadCatchImage(base64Image, docRef.id);
        } catch (uploadErr) {
          console.error('Bildeopplasting feilet, lagrer base64 som fallback:', uploadErr);
          validatedData.image = base64Image; // base64 har size > 0, passerer regelen
        }

        await setDoc(docRef, validatedData);
        return docRef.id;
      } else {
        // Offline — legg i kø (beholder base64 i IndexedDB)
        await offlineQueue.add(validatedData);
        return 'offline-queued';
      }
    } catch (error) {
      // Nettverksfeil — legg i kø
      if (error.code === 'unavailable' || error.message?.includes('network') || !navigator.onLine) {
        try {
          const validatedData = this.validateCatchData(catchData);
          await offlineQueue.add(validatedData);
          return 'offline-queued';
        } catch (queueError) {
          throw new Error('Kunne ikke lagre fangst offline');
        }
      }
      console.error('Lagring feilet:', error);
      throw new Error(`Kunne ikke lagre fangst: ${error.message}`);
    }
  }

  // Synkroniser offline-kø til Firebase
  async syncOfflineQueue() {
    // Hindre parallelle synkroniseringer (race condition)
    if (this._syncing) return 0;
    if (!this.auth.currentUser || !navigator.onLine) return 0;

    this._syncing = true;
    try {
      const pending = await offlineQueue.getAll();
      let synced = 0;

      for (const item of pending) {
        try {
          const { id, queuedAt, ...catchData } = item;

          // Fjern fra kø FØRST for å unngå duplikater ved retry
          await offlineQueue.remove(id);

          // Etterfyll vær hvis fangsten har posisjon men mangler værdata.
          // Været kunne ikke hentes da fangsten ble lagret offline, så vi
          // henter det nå som vi er online igjen (basert på lagrede koordinater).
          catchData.weather = await this.backfillWeather(catchData);

          // Lagre base64-bildet midlertidig
          const base64Image = catchData.image;

          // Sett serverTimestamp på nytt
          catchData.createdAt = serverTimestamp();
          catchData.userId = this.auth.currentUser.uid;

          const catchesRef = collection(
            this.db,
            'artifacts',
            this.appId,
            'users',
            this.auth.currentUser.uid,
            'catches'
          );

          // Generer ID på forhånd og last opp bildet FØR dokumentet skrives,
          // slik at vi aldri lagrer et tomt image-felt (bryter Firestore-regelen).
          const docRef = doc(catchesRef);
          try {
            catchData.image = await this.uploadCatchImage(base64Image, docRef.id);
          } catch (uploadErr) {
            console.error('Bildeopplasting feilet under sync, lagrer base64:', uploadErr);
            catchData.image = base64Image;
          }

          await setDoc(docRef, catchData);

          synced++;
        } catch (err) {
          console.error('Sync feilet for item:', item.id, err);
          // Legg tilbake i køen hvis Firebase-lagring feilet
          try {
            const { id, ...rest } = item;
            await offlineQueue.add(rest);
          } catch (requeueErr) {
            console.error('Kunne ikke legge tilbake i kø:', requeueErr);
          }
          break; // Stopp ved feil, prøv igjen senere
        }
      }
      return synced;
    } finally {
      this._syncing = false;
    }
  }

  // Etterfyll værdata for en fangst som ble lagret offline.
  // Henter vær fra værtjenesten basert på lagret posisjon + tidspunkt,
  // men kun hvis fangsten har posisjon og ikke allerede har værverdier.
  async backfillWeather(catchData) {
    const existing = catchData.weather || {};

    // Hopp over hvis fangsten ikke har posisjon å hente vær for
    if (
      !catchData.location ||
      typeof catchData.location.lat !== 'number' ||
      typeof catchData.location.lng !== 'number'
    ) {
      return existing;
    }

    // Hopp over hvis vær allerede er registrert (manuelt eller hentet tidligere)
    const hasWeather =
      existing.temp != null ||
      existing.wind != null ||
      (existing.condition && existing.condition.length > 0) ||
      (existing.windDir && existing.windDir.length > 0);
    if (hasWeather) return existing;

    try {
      // Bygg tidspunkt fra dato + evt. klokkeslett (samme logikk som i skjemaet)
      let timestamp = catchData.caughtAt || Date.now();
      if (catchData.time && typeof catchData.time === 'string') {
        const date = new Date(timestamp);
        const parts = catchData.time.includes(':')
          ? catchData.time.split(':')
          : [catchData.time.slice(0, 2), catchData.time.slice(2)];
        date.setHours(parseInt(parts[0]) || 0, parseInt(parts[1]) || 0);
        timestamp = date.getTime();
      }

      const weather = await weatherService.fetchWeather(
        catchData.location.lat,
        catchData.location.lng,
        timestamp
      );
      if (!weather) return existing;

      // Slå sammen og rens på nytt slik at det matcher lagringsformatet
      return this.sanitizeWeatherData({
        temp: weather.temp != null ? weather.temp : existing.temp,
        wind: weather.wind != null ? weather.wind : existing.wind,
        condition: weather.condition || existing.condition,
        windDir: weather.windDir || existing.windDir,
      });
    } catch (err) {
      console.error('Etterfylling av vær feilet:', err);
      return existing;
    }
  }

  // Hent brukerens fangster
  subscribeToCatches(callback) {
    if (!this.auth.currentUser) {
      callback([]);
      return () => {};
    }

    const catchesRef = collection(
      this.db, 
      'artifacts', 
      this.appId, 
      'users', 
      this.auth.currentUser.uid, 
      'catches'
    );

    const q = query(
      catchesRef, 
      orderBy('caughtAt', 'desc'),
      limit(100) // Begrens antall for ytelse
    );

    return onSnapshot(q, (snapshot) => {
      const catches = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(catches);
    }, (error) => {
      console.error('Feil ved henting av fangster:', error);
      callback([]);
    });
  }

  // Slett fangst
  async deleteCatch(catchId) {
    if (!this.auth.currentUser) {
      throw new Error('Ikke innlogget');
    }

    try {
      const catchRef = doc(
        this.db, 
        'artifacts', 
        this.appId, 
        'users', 
        this.auth.currentUser.uid, 
        'catches', 
        catchId
      );

      await deleteDoc(catchRef);

      // Rydd opp bilde fra Storage
      try {
        const storageRef = ref(this.storage, `catches/${this.auth.currentUser.uid}/${catchId}/photo.jpg`);
        await deleteObject(storageRef);
      } catch (storageErr) {
        // Ignorer hvis bildet ikke finnes i Storage (kan være gammel base64-fangst)
        console.warn('Kunne ikke slette bilde fra Storage:', storageErr.code);
      }
    } catch (error) {
      console.error('Sletting feilet:', error);
      throw new Error('Kunne ikke slette fangst');
    }
  }

  // Oppdater eksisterende fangst
  async updateCatch(catchId, catchData) {
    if (!this.auth.currentUser) {
      throw new Error('Ikke innlogget');
    }
    if (!catchId) {
      throw new Error('Mangler fangst-ID');
    }
    if (!navigator.onLine) {
      throw new Error('Du må være tilkoblet internett for å redigere en fangst');
    }

    try {
      // Rens og valider felter (bildet kan være uendret, så det kreves ikke her)
      const cleaned = {
        species: this.sanitizeString(catchData.species, 50),
        latin: this.sanitizeString(catchData.latin || '', 100),
        nickname: this.sanitizeString(catchData.nickname || '', 60),
        weight: this.sanitizeNumber(catchData.weight),
        waterType: ['salt', 'fresh'].includes(catchData.waterType) ? catchData.waterType : 'salt',
        caughtAt: this.validateTimestamp(catchData.caughtAt),
        time: this.sanitizeString(catchData.time || '', 10),
        privacy: ['Nøyaktig', '100m', '1km', 'Ingen'].includes(catchData.privacy) ? catchData.privacy : 'Ingen',
        weather: this.sanitizeWeatherData(catchData.weather || {}),
        advanced: this.sanitizeAdvancedData(catchData.advanced || {}),
        updatedAt: serverTimestamp()
      };

      // Posisjon — lagre koordinater, eller nullstill hvis fjernet
      if (catchData.privacy !== 'Ingen' && catchData.location && catchData.location.lat && catchData.location.lng) {
        cleaned.location = {
          lat: parseFloat(catchData.location.lat),
          lng: parseFloat(catchData.location.lng)
        };
      } else {
        cleaned.location = null;
      }

      // Håndter bilde: last kun opp på nytt hvis det er et nytt base64-bilde.
      // Eksisterende bilde (Storage-URL) lar vi være urørt.
      const isNewImage = typeof catchData.image === 'string' && catchData.image.startsWith('data:');
      if (isNewImage) {
        this.validateImageData(catchData.image);
        try {
          const imageUrl = await this.uploadCatchImage(catchData.image, catchId);
          cleaned.image = imageUrl;
        } catch (uploadErr) {
          console.error('Bildeopplasting feilet under oppdatering, beholder base64:', uploadErr);
          cleaned.image = catchData.image;
        }
      }

      const catchRef = doc(
        this.db,
        'artifacts',
        this.appId,
        'users',
        this.auth.currentUser.uid,
        'catches',
        catchId
      );

      await setDoc(catchRef, cleaned, { merge: true });
      return catchId;
    } catch (error) {
      console.error('Oppdatering feilet:', error);
      throw new Error(`Kunne ikke oppdatere fangst: ${error.message}`);
    }
  }

  // Opprett sikker deling
  async createShare(catchData) {
    try {
      const shareId = this.generateSecureId();
      
      // Fjern sensitive data for deling
      const publicData = this.createPublicShareData(catchData);

      // Last opp bilde til offentlig Storage-sti hvis det er en URL fra privat Storage
      if (publicData.image) {
        try {
          // Komprimer bildet for deling (mindre, raskere lasting)
          const shareImage = await compressImage(publicData.image, 'share');
          const imageUrl = await this.uploadSharedImage(shareImage, shareId);
          publicData.image = imageUrl;
        } catch (uploadErr) {
          console.error('Kunne ikke laste opp delt bilde:', uploadErr);
          // Behold original bilde-referanse som fallback
        }
      }
      
      const shareRef = doc(
        this.db, 
        'artifacts', 
        this.appId, 
        'public', 
        'data', 
        'shared_catches', 
        shareId
      );

      await setDoc(shareRef, {
        ...publicData,
        sharedAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 dager
      });

      return shareId;
    } catch (error) {
      console.error('Deling feilet:', error);
      throw new Error('Kunne ikke dele fangst');
    }
  }

  // Hent delt fangst
  async getSharedCatch(shareId) {
    try {
      const shareRef = doc(
        this.db, 
        'artifacts', 
        this.appId, 
        'public', 
        'data', 
        'shared_catches', 
        shareId
      );

      const snapshot = await getDoc(shareRef);
      
      if (!snapshot.exists()) {
        throw new Error('Delt fangst ikke funnet');
      }

      const data = snapshot.data();
      
      // Sjekk om deling har utløpt
      if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
        throw new Error('Delt fangst har utløpt');
      }

      return data;
    } catch (error) {
      console.error('Kunne ikke hente delt fangst:', error);
      throw error;
    }
  }

  // Hjelpefunksjoner for validering og rensing
  sanitizeString(str, maxLength = 100) {
    if (!str || typeof str !== 'string') return '';
    return str
      .replace(/<[^>]*>/g, '') // Fjern HTML-tags
      .trim()
      .slice(0, maxLength);
  }

  // Ikke-negative tall (vekt, lengde, vind osv.)
  sanitizeNumber(num) {
    const parsed = parseFloat(num);
    return isNaN(parsed) || parsed < 0 ? null : Math.min(parsed, 999999);
  }

  // Tall som kan være negative (f.eks. temperatur om vinteren)
  sanitizeSignedNumber(num, min = -100, max = 100) {
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return null;
    return Math.max(min, Math.min(parsed, max));
  }

  validateImageData(image) {
    if (!image || typeof image !== 'string') {
      throw new Error('Ugyldig bildedata');
    }
    
    const base64Regex = /^data:image\/(jpeg|jpg|png|webp);base64,/;
    if (!base64Regex.test(image)) {
      throw new Error('Ugyldig bildeformat');
    }
    
    return image;
  }

  // Last opp bilde til Firebase Storage og returner URL
  async uploadCatchImage(base64Image, catchId) {
    const userId = this.auth.currentUser?.uid;
    if (!userId) throw new Error('Ikke innlogget');

    // Konverter base64 til blob
    const response = await fetch(base64Image);
    const blob = await response.blob();

    const storageRef = ref(this.storage, `catches/${userId}/${catchId}/photo.jpg`);
    // cacheControl: la nettleseren beholde bildet i opptil ett år. Uten dette
    // hentes bildet på nytt hver gang appen åpnes. 'immutable' fordi hvert
    // bilde har en unik sti (catchId) og aldri endres etter opplasting.
    await uploadBytes(storageRef, blob, {
      contentType: 'image/jpeg',
      cacheControl: 'public, max-age=31536000, immutable',
    });
    return getDownloadURL(storageRef);
  }

  // Last opp delt bilde (offentlig tilgjengelig)
  async uploadSharedImage(base64Image, shareId) {
    const response = await fetch(base64Image);
    const blob = await response.blob();

    const storageRef = ref(this.storage, `shared/${shareId}/photo.jpg`);
    await uploadBytes(storageRef, blob, {
      contentType: 'image/jpeg',
      cacheControl: 'public, max-age=31536000, immutable',
    });
    return getDownloadURL(storageRef);
  }

  validateTimestamp(timestamp) {
    const ts = parseInt(timestamp);
    const now = Date.now();
    const oneYearAgo = now - (365 * 24 * 60 * 60 * 1000);
    
    if (isNaN(ts) || ts > now || ts < oneYearAgo) {
      return now;
    }
    
    return ts;
  }

  sanitizeWeatherData(weather) {
    return {
      temp: this.sanitizeSignedNumber(weather.temp, -60, 60),
      wind: this.sanitizeNumber(weather.wind),
      condition: this.sanitizeString(weather.condition, 20),
      windDir: this.sanitizeString(weather.windDir, 50)
    };
  }

  sanitizeAdvancedData(advanced) {
    return {
      length: this.sanitizeNumber(advanced.length),
      circumference: this.sanitizeNumber(advanced.circumference),
      width: this.sanitizeNumber(advanced.width),
      gear: this.sanitizeString(advanced.gear, 100),
      lure: this.sanitizeString(advanced.lure, 100),
      tide: ['Flo', 'Fjære'].includes(advanced.tide) ? advanced.tide : null,
      notes: this.sanitizeString(advanced.notes, 500)
    };
  }

  createPublicShareData(catchData) {
    // Fjern sensitive data for offentlig deling
    const publicData = {
      species: catchData.species,
      latin: catchData.latin,
      weight: catchData.weight,
      waterType: catchData.waterType,
      image: catchData.image,
      caughtAt: catchData.caughtAt,
      weather: {
        temp: catchData.weather?.temp,
        condition: catchData.weather?.condition
      }
    };

    // Fjern nøyaktige koordinater og private notater
    if (catchData.privacy !== 'Nøyaktig') {
      // Legg til maskert posisjon i stedet for nøyaktig
      publicData.locationMasked = catchData.privacy;
    }

    return publicData;
  }

  generateSecureId() {
    return crypto.randomUUID();
  }

  // GDPR-funksjoner
  async exportUserData(userId) {
    if (!this.auth.currentUser || this.auth.currentUser.uid !== userId) {
      throw new Error('Ikke autorisert til å eksportere data');
    }

    try {
      const userData = {
        exportDate: new Date().toISOString(),
        userId: userId,
        catches: [],
        settings: JSON.parse(localStorage.getItem('fiskelykke-privacy-settings') || '{}'),
        consent: JSON.parse(localStorage.getItem('fiskelykke-privacy-consent') || '{}')
      };

      // Hent alle fangster
      const catchesRef = collection(
        this.db, 
        'artifacts', 
        this.appId, 
        'users', 
        userId, 
        'catches'
      );

      const snapshot = await getDocs(catchesRef);
      userData.catches = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Konverter timestamps til lesbare datoer
        caughtAt: doc.data().caughtAt ? new Date(doc.data().caughtAt).toISOString() : null,
        createdAt: doc.data().createdAt ? doc.data().createdAt.toDate().toISOString() : null
      }));

      return userData;
    } catch (error) {
      console.error('Data export failed:', error);
      throw new Error('Kunne ikke eksportere data');
    }
  }

  async deleteAllUserData(userId) {
    if (!this.auth.currentUser || this.auth.currentUser.uid !== userId) {
      throw new Error('Ikke autorisert til å slette data');
    }

    try {
      const batch = writeBatch(this.db);

      // Slett alle fangster
      const catchesRef = collection(
        this.db, 
        'artifacts', 
        this.appId, 
        'users', 
        userId, 
        'catches'
      );

      const catchesSnapshot = await getDocs(catchesRef);
      catchesSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Slett brukerens hoveddokument hvis det eksisterer
      const userRef = doc(this.db, 'artifacts', this.appId, 'users', userId);
      batch.delete(userRef);

      // Utfør batch-sletting
      await batch.commit();

      // Slett lokale data (kun Fiskelykke-nøkler)
      localStorage.removeItem('fiskelykke-privacy-consent');
      localStorage.removeItem('fiskelykke-privacy-settings');
      localStorage.removeItem('fiskelykke-install-dismissed');

      // Logg ut brukeren
      await this.signOut();

    } catch (error) {
      console.error('Data deletion failed:', error);
      throw new Error('Kunne ikke slette alle data');
    }
  }

  // Sjekk om bruker har gitt samtykke
  hasUserConsent() {
    const consent = localStorage.getItem('fiskelykke-privacy-consent');
    if (!consent) return false;
    
    try {
      const parsed = JSON.parse(consent);
      return parsed.accepted === true;
    } catch {
      return false;
    }
  }

  // Initialiser Google Analytics (GA4) — KUN hvis bruker har samtykket.
  // Firebase Analytics-modulen lastes dynamisk slik at verken skript eller
  // _ga-cookie lastes før samtykke er gitt. Trygt å kalle flere ganger.
  async initAnalytics() {
    // Krever samtykke og en measurementId i konfigurasjonen
    if (!this.hasUserConsent()) return null;
    if (!process.env.REACT_APP_FIREBASE_MEASUREMENT_ID) return null;
    if (this.analytics) return this.analytics;

    try {
      const { isSupported, getAnalytics, setAnalyticsCollectionEnabled } =
        await import('firebase/analytics');

      // isSupported() filtrerer ut miljøer uten støtte (f.eks. SSR/enkelte in-app-browsere)
      const supported = await isSupported();
      if (!supported) return null;

      this.analytics = getAnalytics(this.app);
      setAnalyticsCollectionEnabled(this.analytics, true);
      return this.analytics;
    } catch (err) {
      console.error('Analytics kunne ikke initialiseres:', err);
      return null;
    }
  }

  // Logg en hendelse hvis analytics er aktivt (no-op uten samtykke)
  async logAnalyticsEvent(eventName, params = {}) {
    if (!this.analytics) return;
    try {
      const { logEvent } = await import('firebase/analytics');
      logEvent(this.analytics, eventName, params);
    } catch (err) {
      console.error('Kunne ikke logge analytics-hendelse:', err);
    }
  }

  // Hent brukerens personverninnstillinger
  getPrivacySettings() {
    const settings = localStorage.getItem('fiskelykke-privacy-settings');
    if (!settings) {
      return {
        dataCollection: true,
        locationSharing: '1km',
        analyticsOptIn: false,
        marketingEmails: false
      };
    }
    
    try {
      return JSON.parse(settings);
    } catch {
      return {
        dataCollection: true,
        locationSharing: '1km',
        analyticsOptIn: false,
        marketingEmails: false
      };
    }
  }

  // Anonymiser data for analyse (hvis bruker har samtykket)
  createAnalyticsData(catchData) {
    const settings = this.getPrivacySettings();
    
    if (!settings.analyticsOptIn) {
      return null;
    }

    // Returner kun anonyme, aggregerte data
    return {
      species: catchData.species,
      waterType: catchData.waterType,
      month: new Date(catchData.caughtAt).getMonth(),
      year: new Date(catchData.caughtAt).getFullYear(),
      hasWeight: !!catchData.weight,
      hasAdvancedData: !!(catchData.advanced?.gear || catchData.advanced?.lure),
      region: this.getGeneralRegion(catchData.privacy) // Kun generell region
    };
  }

  getGeneralRegion(privacyLevel) {
    // Returner kun svært generell region basert på personvernnivå
    if (privacyLevel === 'Nøyaktig') return null;
    // Her kunne du implementere logikk for å returnere fylke/landsdel
    return 'Norge'; // Placeholder
  }

  // Lagre kontaktmelding/feedback
  async submitContactMessage(messageData) {
    try {
      const validatedData = {
        email: this.sanitizeString(messageData.email, 100),
        message: this.sanitizeString(messageData.message, 1000),
        type: messageData.type || 'feedback',
        submittedAt: serverTimestamp(),
        userId: this.auth.currentUser?.uid || 'anonymous'
      };

      const messagesRef = collection(
        this.db,
        'artifacts',
        this.appId,
        'public',
        'data',
        'contact_messages'
      );

      const docRef = await addDoc(messagesRef, validatedData);
      return docRef.id;
    } catch (error) {
      console.error('Kunne ikke sende melding:', error);
      throw new Error('Kunne ikke sende melding. Prøv igjen senere.');
    }
  }
}

export default new FirebaseService();